import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { searchLocations, type LocationOption } from '../../api/locationsApi';
import { useQuery } from '@tanstack/react-query';
import {
  useChannelScopes,
  useDeleteLocationGroup,
  useLocationGroups,
  useStoreScopes,
  useUpsertLocationGroup,
} from '../../api/hooks';
import type { LocationGroup } from '../../model/types';

interface GroupsPanelProps {
  canManage: boolean;
  onToast: (message: string, severity?: 'success' | 'error' | 'warning') => void;
}

/** "Location groups" tab — LocationGroup CRUD (design-stock-channels-page §8.1). */
export function GroupsPanel({ canManage, onToast }: GroupsPanelProps) {
  const { t } = useTranslation('channelStock');
  const groups = useLocationGroups();
  const channelScopes = useChannelScopes();
  const storeScopes = useStoreScopes();
  const upsert = useUpsertLocationGroup();
  const remove = useDeleteLocationGroup();

  const locationsQuery = useQuery({
    queryKey: ['channel-stock', 'locations'],
    queryFn: () => searchLocations(),
  });
  const locationById = useMemo(
    () => new Map((locationsQuery.data ?? []).map((location) => [location.id, location])),
    [locationsQuery.data],
  );

  const [search, setSearch] = useState('');
  const [dialogGroup, setDialogGroup] = useState<LocationGroup | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<LocationGroup | null>(null);

  const usage = useMemo(() => {
    const map = new Map<string, { channels: number; stores: number }>();
    (channelScopes.data ?? []).forEach((scope) => {
      const entry = map.get(scope.groupId) ?? { channels: 0, stores: 0 };
      entry.channels += 1;
      map.set(scope.groupId, entry);
    });
    (storeScopes.data ?? []).forEach((scope) => {
      const entry = map.get(scope.groupId) ?? { channels: 0, stores: 0 };
      entry.stores += 1;
      map.set(scope.groupId, entry);
    });
    return map;
  }, [channelScopes.data, storeScopes.data]);

  if (groups.isLoading) return <LoadingState />;
  if (groups.isError) return <ErrorState onRetry={() => void groups.refetch()} />;

  const rows = (groups.data ?? []).filter(
    (group) =>
      !search ||
      group.name.toLowerCase().includes(search.toLowerCase()) ||
      group.code.toLowerCase().includes(search.toLowerCase()),
  );

  const createButton = (
    <Button
      variant="contained"
      startIcon={<AddRoundedIcon />}
      disabled={!canManage}
      onClick={() => setDialogGroup('new')}
    >
      {t('groups.create')}
    </Button>
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('groups.hint')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <TextField
          size="small"
          sx={{ maxWidth: 320 }}
          placeholder={t('groups.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('refresh')}>
            <IconButton onClick={() => void groups.refetch()} aria-label="refresh-groups">
              <RefreshRoundedIcon />
            </IconButton>
          </Tooltip>
          {canManage ? createButton : (
            <Tooltip title={t('noPermissionTooltip')}>
              <span>{createButton}</span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {groups.isFetching && !groups.isLoading ? <LinearProgress sx={{ height: 4 }} /> : <span style={{ height: 4, display: 'block' }} />}

      {rows.length === 0 ? (
        search ? (
          <Typography color="text.secondary">{t('groups.nothingFound')}</Typography>
        ) : (
          <EmptyState
            icon={<CallSplitRoundedIcon />}
            title={t('groups.empty.title')}
            description={t('groups.empty.description')}
            action={canManage ? createButton : undefined}
          />
        )
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('groups.columns.name')}</TableCell>
                <TableCell>{t('groups.columns.code')}</TableCell>
                <TableCell>{t('groups.columns.locations')}</TableCell>
                <TableCell>{t('groups.columns.usage')}</TableCell>
                <TableCell align="right">{t('columns.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((group) => {
                const used = usage.get(group.id);
                const usedLabel = used
                  ? t('groups.usageValue', { channels: used.channels, stores: used.stores })
                  : '—';
                const deleteDisabled = !canManage || Boolean(used);
                return (
                  <TableRow key={group.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{group.name}</TableCell>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace' }}>{group.code}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {group.locationIds.slice(0, 3).map((locationId) => {
                          const location = locationById.get(locationId);
                          return (
                            <Chip
                              key={locationId}
                              size="small"
                              variant="outlined"
                              label={location ? `${location.name} (${location.code})` : locationId.slice(0, 8)}
                            />
                          );
                        })}
                        {group.locationIds.length > 3 ? (
                          <Tooltip
                            title={group.locationIds
                              .map((id) => locationById.get(id)?.name ?? id)
                              .join(', ')}
                          >
                            <Chip size="small" label={`+${group.locationIds.length - 3}`} />
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{usedLabel}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={canManage ? t('groups.edit') : t('noPermissionTooltip')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!canManage}
                            aria-label={`edit-group-${group.code}`}
                            onClick={() => setDialogGroup(group)}
                          >
                            <EditRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !canManage
                            ? t('noPermissionTooltip')
                            : used
                              ? t('groups.deleteBlocked')
                              : t('groups.delete')
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={deleteDisabled}
                            aria-label={`delete-group-${group.code}`}
                            onClick={() => setConfirmDelete(group)}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {dialogGroup !== null ? (
        <GroupDialog
          group={dialogGroup === 'new' ? null : dialogGroup}
          locations={locationsQuery.data ?? []}
          pending={upsert.isPending}
          onClose={() => setDialogGroup(null)}
          onSave={(body) =>
            upsert.mutate(body, {
              onSuccess: () => {
                setDialogGroup(null);
                onToast(t('groups.saved'));
              },
              onError: (error) => onToast(error.message, 'error'),
            })
          }
        />
      ) : null}

      <Dialog
        open={confirmDelete !== null}
        onClose={remove.isPending ? undefined : () => setConfirmDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t('groups.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('groups.deleteConfirmBody', { name: confirmDelete?.name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={remove.isPending}
            startIcon={remove.isPending ? <CircularProgress size={16} /> : undefined}
            onClick={() => {
              if (!confirmDelete) return;
              remove.mutate(confirmDelete.id, {
                onSuccess: () => {
                  setConfirmDelete(null);
                  onToast(t('groups.deleted'));
                },
                onError: (error) => onToast(error.message, 'error'),
              });
            }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

interface GroupDialogProps {
  group: LocationGroup | null;
  locations: LocationOption[];
  pending: boolean;
  onClose: () => void;
  onSave: (body: { code: string; name: string; locationIds: string[] }) => void;
}

function GroupDialog({ group, locations, pending, onClose, onSave }: GroupDialogProps) {
  const { t } = useTranslation('channelStock');
  const [name, setName] = useState(group?.name ?? '');
  const [code, setCode] = useState(group?.code ?? '');
  const [selected, setSelected] = useState<LocationOption[]>(
    locations.filter((location) => group?.locationIds.includes(location.id)),
  );

  const valid = name.trim().length > 0 && code.trim().length > 0 && selected.length > 0;

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={pending ? undefined : onClose}>
      <DialogTitle>{group ? t('groups.dialogEditTitle') : t('groups.dialogCreateTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('groups.nameLabel')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            autoFocus
          />
          <TextField
            label={t('groups.codeLabel')}
            value={code}
            onChange={(event) => setCode(event.target.value)}
            required
            disabled={Boolean(group)}
            helperText={group ? t('groups.codeImmutable') : t('groups.codeHelper')}
            slotProps={{ htmlInput: { style: { fontFamily: '"JetBrains Mono", monospace' } } }}
          />
          <Autocomplete
            multiple
            options={locations}
            value={selected}
            onChange={(_event, value) => setSelected(value)}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('groups.locationsLabel')}
                required={selected.length === 0}
                helperText={t('groups.locationsHelper')}
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>{t('cancel')}</Button>
        <Button
          variant="contained"
          disabled={!valid || pending}
          startIcon={pending ? <CircularProgress size={16} /> : undefined}
          onClick={() => onSave({ code: code.trim(), name: name.trim(), locationIds: selected.map((l) => l.id) })}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
